// src/hooks/useDataSync.js
import { useState, useEffect } from "react";
import { collection, onSnapshot, writeBatch, doc } from "firebase/firestore";
import {
  DEFAULT_SOFA_STYLES,
  DEFAULT_CUSHION_THICKNESSES,
  DEFAULT_CARPET_STYLES,
  DEFAULT_EDGING_CARPET_STYLES,
  DEFAULT_WALL_STICKER_STYLES,
  DEFAULT_APRON_STYLES,
} from "../data";

const useDataSync = (db, appId, IS_TEST_MODE) => {
  const [sofaStyles, setSofaStyles] = useState(
    DEFAULT_SOFA_STYLES.map((s, i) => ({ id: String(i), ...s, order: i }))
  );
  const [cushionThicknesses, setCushionThicknesses] = useState(
    DEFAULT_CUSHION_THICKNESSES.map((s, i) => ({ id: String(i), ...s }))
  );
  const [carpetStyles, setCarpetStyles] = useState(
    DEFAULT_CARPET_STYLES.map((s, i) => ({ id: String(i), ...s, order: i }))
  );
  const [edgingCarpetStyles, setEdgingCarpetStyles] = useState(
    DEFAULT_EDGING_CARPET_STYLES.map((s, i) => ({
      id: String(i),
      ...s,
      order: i,
    }))
  );
  const [wallStickerStyles, setWallStickerStyles] = useState(
    DEFAULT_WALL_STICKER_STYLES.map((s, i) => ({
      id: String(i),
      ...s,
      order: i,
    }))
  );
  const [apronStyles, setApronStyles] = useState(
    DEFAULT_APRON_STYLES.map((s, i) => ({ id: String(i), ...s, order: i }))
  );
  const [history, setHistory] = useState([]);

  useEffect(() => {
    // 如果沒有 appId 且不是測試模式，或者沒有 db，則不執行同步
    if ((!appId && !IS_TEST_MODE) || !db) return;

    // 確保有目標 ID (測試模式下若 appId 為空則使用預設值)
    const targetId = appId || "test-app-id";

    // 監聽歷史報價
    const unsubQ = onSnapshot(
      collection(db, "artifacts", targetId, "public", "data", "quotes"),
      (s) =>
        setHistory(
          s.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort(
              (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
            )
        )
    );

    // 通用同步函數
    const sync = (col, def, set) =>
      onSnapshot(
        collection(db, "artifacts", targetId, "public", "data", col),
        (s) => {
          if (s.empty) {
            // 若無資料則寫入預設值
            const b = writeBatch(db);
            def.forEach((d, i) =>
              b.set(
                doc(
                  collection(db, "artifacts", targetId, "public", "data", col)
                ),
                { ...d, order: i }
              )
            );
            b.commit();
          } else {
            // 有資料則讀取
            const currentData = s.docs.map((d) => ({ id: d.id, ...d.data() }));

            // 注意：不再依「名稱比對」自動補寫預設款式。
            // 舊做法會把管理員「刪除」的預設款式在下次 snapshot 自動復活，
            // 也會在「改名」時因原名不存在而重複塞回一份，造成共用資料永久汙染。
            // 預設款式只在集合為空時（上方 s.empty 分支）建立一次；
            // 之後要新增款式請透過款式管理 UI，由管理員手動新增。

            // 更新 State
            set(currentData.sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
          }
        }
      );

    const un1 = sync("sofa_styles", DEFAULT_SOFA_STYLES, setSofaStyles);
    const un2 = sync("cushion_thicknesses", DEFAULT_CUSHION_THICKNESSES, (d) =>
      setCushionThicknesses(d.sort((a, b) => a.cm - b.cm))
    );
    const un3 = sync("carpet_styles", DEFAULT_CARPET_STYLES, setCarpetStyles);
    const un4 = sync(
      "edging_carpet_styles",
      DEFAULT_EDGING_CARPET_STYLES,
      setEdgingCarpetStyles
    );
    const un5 = sync(
      "wall_sticker_styles",
      DEFAULT_WALL_STICKER_STYLES,
      setWallStickerStyles
    );
    const un6 = sync("apron_styles", DEFAULT_APRON_STYLES, setApronStyles);

    return () => {
      unsubQ();
      un1();
      un2();
      un3();
      un4();
      un5();
      un6();
    };
  }, [db, appId, IS_TEST_MODE]);

  return {
    sofaStyles,
    setSofaStyles,
    cushionThicknesses,
    setCushionThicknesses,
    carpetStyles,
    setCarpetStyles,
    edgingCarpetStyles,
    setEdgingCarpetStyles,
    wallStickerStyles,
    setWallStickerStyles,
    apronStyles,
    setApronStyles,
    history,
    setHistory,
  };
};

export default useDataSync;
